<template>
  <f-box 
    class="stat p-7 p-bottom-0 has-row-10 has-no-col-gap has-no-row-gap" 
    grid
  >
    <div class="stat-ingoings-chart is-col-12 is-row-9">
      <div class="chart-container" ref="chartContainer"></div>
    </div>

    <div class="is-col-12 is-aligned">
      <f-button 
        v-for="(option, index) in optionsData" 
        :key="index" 
        @click="updateOptionsData(option)"
      >
        {{ index }}
      </f-button>

      <f-datepicker v-model="from"></f-datepicker>
      <f-datepicker v-model="to"></f-datepicker>
    </div>
  </f-box>
</template>

<script>
import { onMounted, nextTick, ref, onUnmounted, computed, watch, reactive, toRefs } from 'vue'
import useStatistics from '@composables/statistics/use-statistics.js'
import useLineChart from '@composables/statistics/charts/use-line-chart.js'
import useNumbers from '@composables/use-numbers'
import dayjs from 'dayjs'
import Chart from 'apexcharts'

export default {
  setup() {
    const { displayAmount, isNumber } = useNumbers()

    const state = reactive({
      to: new Date(),
      from: new Date(),
    })

    state.from.setYear(state.to.getFullYear() - 1)

    const {
      newSuscribedUsersByMonth,
      newFreeUsersByMonth,
      endSuscribedUsersByMonth,
      endFreeUsersByMonth,
      rangeMonths,
      period,
      year: currentYear
    } = useStatistics(state)

    const chartOptions = computed(() => {
      return {
        chart: {},
        colors: [
          '#6fbbff', 
          '#ff2d55',
          'rgba(111, 187, 255, 0.5)',
          'rgba(255, 45, 85, 0.5)', 
        ],
        dataLabels: {
          enabled: false
        },
        series: [{
          type: 'line',
          name: 'debuts',
          data: newSuscribedUsersByMonth.value || [],
        }, {
          type: 'line',
          name: 'debuts',
          data: newFreeUsersByMonth.value || [],
        }, {
          type: 'column',
          name: 'fins',
          data: endSuscribedUsersByMonth.value || [],
        }, {
          type: 'column',
          name: 'fins',
          data: endFreeUsersByMonth.value || [],
        }],
        stroke: {
          curve: 'smooth',
          width: [2, 2, 0, 0]
        },
        plotOptions: {
          bar: {
            columnWidth: '20%',
          },
        },
        xaxis: {
          categories: rangeMonths.value || [],
          labels: {
            formatter: function (value) {
              return value?.month >= 0 ? dayjs().month(value.month).year(value.year).format('MMM YY') : 'NOTHING'
            }
          },
        },
        yaxis: [{
          seriesName: 'fins',
          title: {
            text: 'Fins gratuits & payants (barres)',
          },
          labels: {
            show: true
          },
          decimalsInFloat: false,
        }, {
          seriesName: 'fins',
          show: false,
        }, {
          seriesName: 'debuts',
          title: {
            text: 'Débuts gratuits & payants (lignes)',
          },
          labels: {
            show: true
          },
          decimalsInFloat: false,
          opposite: true,
        }, {
          seriesName: 'debuts',
          show: false,
        }],
        legend: {
          show: false
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ series, seriesIndex, dataPointIndex, w }) {
            return `
                <div class="stat-detail">
                  <p>
                    <b>
                      <small>
                        ${period?.value?.months[dataPointIndex] || ''}
                      </small>
                    </b>
                    <br>

                    <span style="color: ${w.config.colors[0]}">
                      <b>${displayAmount(series[0][dataPointIndex])}</b> débuts d'abonnements
                    </span>
                    <br>
                    <span style="color: ${w.config.colors[1]}">
                      <b>${displayAmount(series[1][dataPointIndex])}</b> débuts de gratuité
                    </span>
                    <br>
                    <span style="color: ${w.config.colors[2]}">
                      <b>${displayAmount(series[2][dataPointIndex])}</b> fins d'abonnements
                    </span>
                    <br>
                    <span style="color: ${w.config.colors[3]}">
                      <b>${displayAmount(series[3][dataPointIndex])}</b> fins de gratuité
                    </span>
                  </p>
                </div>
              `
          }
        }
      }
    })

    function removeMonths(date, months) {
      date.setMonth(date.getMonth() - months)
      return date
    }

    const optionsData = {
      '12 mois': 11,
      '6 mois': 5,
      '3 mois': 2
    }

    function updateOptionsData (option) {
      state.from = removeMonths(new Date(), option),
      state.to = new Date()
    }

    const { chartContainer } = useLineChart(chartOptions)

    return {
      chartContainer,
      ...toRefs(state),
      updateOptionsData,
      optionsData,
    }
  }
}
</script>

<style lang="scss">
.stat {
  position: relative;

  h6,
  p {
    margin: 0;
  }
}

.stat-ingoings-chart {
  position: relative;
  height: 100%;
  width: 100%;
  cursor: pointer;
}

.chart-container {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
}

.stat-detail {
  border: none;
  box-shadow: 0 1px 1px 0 rgba(102, 166, 255, 0.1);
  background: $white;
  color: $blue-dark;
  padding: 0.3rem 1rem;
  min-width: 150px;
}
</style>
